import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PlexusBackground from "../components/PlexusBackground"; // Import PlexusBackground
import CookieDisclaimer from "../components/CookieDisclaimer";
import videoData from '../data/videos.json';
import "../styles.css";

// --- Manually add YouTube videos here ---
const manualVideos = [
  {
    id: { videoId: "dQw4w9WgXcQ" },
    snippet: {
      title: "Example Video 1",
      publishedAt: "2024-04-01T00:00:00Z",
      thumbnails: { high: { url: "https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg" } }
    }
  },
  {
    id: { videoId: "eY52Zsg-KVI" },
    snippet: {
      title: "Example Video 2",
      publishedAt: "2024-03-15T00:00:00Z",
      thumbnails: { high: { url: "https://img.youtube.com/vi/eY52Zsg-KVI/hqdefault.jpg" } }
    }
  },
  {
    id: { videoId: "M7lc1UVf-VE" },
    snippet: {
      title: "Example Video 3",
      publishedAt: "2024-02-20T00:00:00Z",
      thumbnails: { high: { url: "https://img.youtube.com/vi/M7lc1UVf-VE/hqdefault.jpg" } }
    }
  },
  // Add more videos as needed
];

export default function Home() {
  // State for YouTube videos
  const [videos, setVideos] = useState(videoData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for featured products
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/products.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const allProducts = await response.json();

        // Map all products to the required format
        const formattedProducts = allProducts.map(p => ({
          id: p.id,
          img: p.overlayImage,
          alt: p.name,
          tag: (p.cost || 'free').toLowerCase(),
          title: p.name,
          description: p.description,
          link: p.buttonLink,
          btnClass: p.cost === 'Premium' ? 'btn btn-product-premium2' : 'btn btn-product',
          btnText: p.buttonText, // Use dynamic button text
          featured: p.featured,
        }));

        // Filter for featured products, or fallback to the first 3 if none are featured
        const featured = formattedProducts.filter(p => p.featured);
        setFeaturedProducts(featured.length > 0 ? featured.slice(0, 3) : formattedProducts.slice(0, 3));

      } catch (e) {
        console.error("Failed to fetch products for home page:", e);
        setProductsError("Failed to load featured tools. Please ensure 'npm run fetch-products' was successful and try again.");
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);


  return (
    <>
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="hero" style={{position: 'relative', overflow: 'hidden'}}>
          <div className="container">
            <div className="hero-content">
              <h1>ZEMAXI: <span className="highlight">Streamer & Tool Creator</span></h1>
              <p>Welcome to my digital hub for streaming entertainment and powerful tools for content creators. Explore my Twitch streams, YouTube content, and discover tools that will take your streaming setup to the next level.</p>
              <div className="hero-buttons">
                <Link to="/stream" className="btn btn-primary">Watch Live on Twitch</Link>
                <Link to="/shop" className="btn btn-secondary">Browse Stream Tools</Link>
              </div>
            </div>
          </div>
          <div className="hero-background">
            <div className="grid"></div>
            <div className="glowing-lines"></div>
            {/* Plexus animated background */}
            <PlexusBackground />
          </div>
        </section>

        <section className="featured">
          <div className="container">
            <h2>Currently <span className="highlight">Streaming</span></h2>
            <div className="twitch-embed">
              <iframe
                src="https://player.twitch.tv/?channel=zemaxi&parent=localhost&parent=zemaxi.com"
                height="700"
                width="100%"
                allowFullScreen
                frameBorder="0"
                scrolling="no"
              />
            </div>
          </div>
        </section>

        <section className="latest-videos">
          <div className="container">
            <h2>Latest <span className="highlight">Videos</span></h2>
            <div className="video-grid" id="youtube-videos">
              {loading && (
                <div className="video-loading">
                  <div className="spinner"></div>
                  <p>Loading latest videos...</p>
                </div>
              )}
              {error && (
                <div className="video-loading">
                  <p style={{ color: 'red' }}>Failed to load videos: {error}</p>
                </div>
              )}
              {!loading && !error && videos.length === 0 && (
                <div className="video-loading">
                  <p>No videos found.</p>
                </div>
              )}
              {/* Show only the first 3 manual videos */}
              {!loading && !error && videos.slice(0, 3).map(video => (
                <div className="video-item" key={video.id.videoId}>
                  <a href={`https://www.youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer">
                    <div className="video-thumbnail">
                      <img src={video.snippet.thumbnails.high.url} alt={video.snippet.title} />
                    </div>
                    <div className="video-info">
                      <h3>{video.snippet.title}</h3>
                      <div className="video-meta">
                        <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
            <div className="section-cta">
              <Link to="/videos" className="btn btn-secondary">View All Videos</Link>
            </div>
          </div>
        </section>
        <section className="featured-products">
          <div className="container">
            <h2>Featured <span className="highlight">Tools</span></h2>
            <div className="product-grid">
              {productsLoading ? (
                <div className="shop-loading">
                  <div className="spinner"></div>
                  <p>Loading tools...</p>
                </div>
              ) : productsError ? (
                <div className="shop-error">
                  <p>{productsError}</p>
                </div>
              ) : (
                featuredProducts.map((product, idx) => (
                  <div className="product-card" key={product.id || idx}>
                    <div className="product-image">
                      <img src={product.img} alt={product.alt} />
                    </div>
                    <div className="product-info">
                      <div className={`product-tag ${product.tag}`}>{product.tag === "premium" ? "Premium" : product.tag.charAt(0).toUpperCase() + product.tag.slice(1)}</div>
                      <h3>{product.title}</h3>
                      <p>{product.description}</p>
                      <a href={product.link} className={product.btnClass} target="_blank" rel="noopener noreferrer">{product.btnText}</a>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="section-cta">
              <Link to="/shop" className="btn btn-secondary">Browse All Tools</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
